import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ProjectDeleteDialog } from "@/components/project-delete-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type OutOfSyncProject, type ProjectSummary, queryKeys, useProjects } from "@/lib/api";
import { logger } from "@/lib/logger";

interface OutOfSyncDialogProps {
  projects: OutOfSyncProject[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolved: () => void;
}

export function OutOfSyncDialog({
  projects,
  open,
  onOpenChange,
  onResolved,
}: OutOfSyncDialogProps) {
  const queryClient = useQueryClient();
  // The delete dialog needs each project's full row; admins list every project.
  const { data: allProjects = [], isLoading: projectsLoading } = useProjects(open);
  const [remaining, setRemaining] = useState<OutOfSyncProject[]>(projects);
  // The project whose teardown is open. Every listed project still links its
  // repo, so the record cannot be removed on its own (BOIE-338): it goes
  // through the same "Delete all" as the admin Projects page.
  const [deleteTarget, setDeleteTarget] = useState<ProjectSummary | null>(null);
  const [rowError, setRowError] = useState<{ projectId: number; message: string } | null>(null);
  const prevRemainingLen = useRef(remaining.length);

  // Keep remaining in sync when the dialog opens with new projects,
  // and reset the previous-length ref so transition tracking starts fresh.
  useEffect(() => {
    if (!open) {
      prevRemainingLen.current = 0;
      return;
    }
    if (projects.length > 0) {
      setRemaining(projects);
      prevRemainingLen.current = projects.length;
    }
  }, [open, projects]);

  // Auto-resolve only on an actual transition from non-empty -> empty remaining
  useEffect(() => {
    if (!open) {
      return;
    }
    const becameEmpty = prevRemainingLen.current > 0 && remaining.length === 0;
    prevRemainingLen.current = remaining.length;
    if (becameEmpty) {
      onResolved();
    }
  }, [open, remaining.length, onResolved]);

  const handleDelete = (project: OutOfSyncProject) => {
    // One teardown at a time: a second row's Delete must never replace (and
    // unmount) the delete dialog that is open, or a running teardown's
    // outcome is lost.
    if (deleteTarget !== null) {
      logger.debug("[out-of-sync] delete ignored, a teardown is open for", deleteTarget.id);
      return;
    }
    const summary = allProjects.find((p) => p.id === project.id);
    if (!summary) {
      logger.warn("[out-of-sync] project missing from the project list:", project.id);
      setRowError({
        projectId: project.id,
        message: "This project could not be loaded. Reload the page and try again.",
      });
      return;
    }
    setRowError(null);
    setDeleteTarget(summary);
  };

  const handleClose = (v: boolean) => {
    if (!v) {
      // Only treat as "resolved" if all items were actually deleted;
      // otherwise just dismiss without triggering a full refresh.
      if (remaining.length === 0 && projects.length > 0) {
        onResolved();
      }
      prevRemainingLen.current = 0;
      setRemaining([]);
      setRowError(null);
    }
    onOpenChange(v);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-500" />
              Out-of-sync projects found
            </DialogTitle>
            <DialogDescription>
              These projects are in ScaffoldUI, but their GitHub repositories could not be found.
              Check each repo before deleting. Delete opens the project&apos;s teardown: it deletes
              everything still connected to the project, then removes the project record. If
              something cannot be deleted, it is named there and the record is kept.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {remaining.map((project) => (
                <div key={project.id} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-sm font-medium">
                        {project.project_name}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {project.project_slug}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {project.github_url && (
                        <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                          <Button size="icon" variant="ghost" className="size-8">
                            <ExternalLink className="size-3.5" />
                          </Button>
                        </a>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-8 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
                        onClick={() => handleDelete(project)}
                        disabled={projectsLoading}
                        aria-label={`Delete ${project.project_name}`}
                        title="Delete"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                  {rowError?.projectId === project.id && (
                    <p className="text-destructive mt-2 text-xs">{rowError.message}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => handleClose(false)}>
                Done
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <ProjectDeleteDialog
        key={deleteTarget?.id ?? "none"}
        project={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setDeleteTarget(null);
          }
        }}
        onResourcesChanged={() =>
          void queryClient.invalidateQueries({ queryKey: queryKeys.projects })
        }
        onProjectDeleted={() => {
          const deletedId = deleteTarget?.id;
          setRemaining((prev) => prev.filter((p) => p.id !== deletedId));
        }}
      />
    </>
  );
}
